import { apiUrl } from "../utils/api";

export interface AppUser {
    id: string;
    email?: string | null;
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    username?: string | null;
    department?: string | null;
    /** Single better-auth role label: 'admin' | 'gfgl' | 'mitarbeiter' | 'service'. */
    role?: string | null;
}

/** Human-readable label for a user. */
export const userDisplay = (u: AppUser): string => {
    const last = u.lastName || "";
    const first = u.firstName || "";
    if (last && first) return `${last}, ${first}`;
    return last || first || u.name || u.username || u.email || u.id;
};

/**
 * Display label for a stored user identifier (id / email / display string).
 * Use this in pickers and any place a stored user-key needs to be shown.
 * Falls back to a short hash form so we never surface a 32-char raw id.
 */
export const labelForUserKey = (
    key: string,
    users: AppUser[],
    opts?: { loading?: boolean },
): string => {
    if (!key) return "";
    const lc = key.trim().toLowerCase();
    const hit = users.find(
        (u) =>
            u.id.toLowerCase() === lc ||
            (u.email ?? "").toLowerCase() === lc ||
            (u.username ?? "").toLowerCase() === lc ||
            userDisplay(u).toLowerCase() === lc,
    );
    if (hit) return userDisplay(hit);
    if (opts?.loading || users.length === 0) return "…";
    // Looks like a better-auth-style random id?
    if (/^[A-Za-z0-9]{20,}$/.test(key)) {
        return `${key.slice(0, 6)}…`;
    }
    return key;
};

/**
 * Resolve a stored user identifier (id, email, Kürzel/`username` or display)
 * to a user record. Das Kürzel gehört seit T-85 dazu (wie in
 * `labelForUserKey`): Alt-Einträge, die als Kürzel gespeichert sind, bekamen
 * sonst keine User-Id und damit nie ein Foto.
 */
export const findUserBy = (
    users: AppUser[],
    needle: string,
): AppUser | undefined => {
    const lc = needle.trim().toLowerCase();
    if (!lc) return undefined;
    return users.find(
        (u) =>
            u.id.toLowerCase() === lc ||
            (u.email ?? "").toLowerCase() === lc ||
            (u.username ?? "").toLowerCase() === lc ||
            userDisplay(u).toLowerCase() === lc,
    );
};

/**
 * Cached list — short TTL so re-renders are cheap but newly seeded users don't
 * get stuck forever. Backed by the public `/users` endpoint (accessible to any
 * authenticated tenant user), not the admin-gated better-auth `admin.listUsers`.
 */
const CACHE_TTL_MS = 60_000;
let _cache: { at: number; users: AppUser[] } | null = null;
let _inflight: Promise<AppUser[]> | null = null;

export const listAppUsers = async (force = false): Promise<AppUser[]> => {
    const now = Date.now();
    if (!force && _cache && now - _cache.at < CACHE_TTL_MS) {
        return _cache.users;
    }
    if (_inflight) return _inflight;

    _inflight = (async () => {
        try {
            const res = await fetch(apiUrl("users"));
            if (!res.ok) throw new Error(`Users list HTTP ${res.status}`);
            const data = (await res.json()) as AppUser[];
            const users = Array.isArray(data) ? data : [];
            _cache = { at: Date.now(), users };
            return users;
        } catch (err) {
            console.error("listAppUsers failed", err);
            return _cache?.users ?? [];
        } finally {
            _inflight = null;
        }
    })();
    return _inflight;
};

export const refreshAppUsers = () => listAppUsers(true);

/**
 * Serverseitig gelernte zuletzt erwähnte Empfänger des aktuellen Users
 * (User-IDs, jüngste zuerst) — geräteübergreifend, KEIN localStorage (AC7).
 * Wird beim @-Autocomplete genutzt, um vertraute Empfänger oben vorzuschlagen.
 * Backend: `GET /user-preference/notes/recent-mentions`. Best-effort: bei Fehler
 * leere Liste, der Composer fällt dann auf die reine Namenssuche zurück.
 */
const RECENT_MENTIONS_TTL_MS = 60_000;
let _recentMentionsCache: { at: number; ids: string[] } | null = null;
let _recentMentionsInflight: Promise<string[]> | null = null;

export const listRecentMentions = async (force = false): Promise<string[]> => {
    const now = Date.now();
    if (!force && _recentMentionsCache && now - _recentMentionsCache.at < RECENT_MENTIONS_TTL_MS) {
        return _recentMentionsCache.ids;
    }
    if (_recentMentionsInflight) return _recentMentionsInflight;

    _recentMentionsInflight = (async () => {
        try {
            const res = await fetch(apiUrl("user-preference/notes/recent-mentions"));
            if (!res.ok) throw new Error(`Recent mentions HTTP ${res.status}`);
            const data = (await res.json()) as unknown;
            const ids = Array.isArray(data) ? data.map((x) => String(x)).filter(Boolean) : [];
            _recentMentionsCache = { at: Date.now(), ids };
            return ids;
        } catch (err) {
            console.error("listRecentMentions failed", err);
            return _recentMentionsCache?.ids ?? [];
        } finally {
            _recentMentionsInflight = null;
        }
    })();
    return _recentMentionsInflight;
};
